/**
 * Static-frame NES PPU model.
 *
 * This isn't a cycle-accurate emulator — there's no CPU, no scanline timing,
 * no DMA, no sprite-zero hit. It implements the *layout and rendering* of a
 * single frame given a complete set of pattern / nametable / attribute /
 * palette / scroll / OAM state. Glitches happen by mutating that state
 * directly (see `nesGlitch.ts`).
 */

import { nesIndexToPackedRgba } from "./nesPalette";

export const NES_SCREEN_WIDTH = 256;
export const NES_SCREEN_HEIGHT = 240;

export const PATTERN_TABLE_BYTES = 4096; // 256 tiles × 16 bytes (2 bitplanes)
export const NAMETABLE_TILES_X = 32;
export const NAMETABLE_TILES_Y = 30;
export const NAMETABLE_TILES = NAMETABLE_TILES_X * NAMETABLE_TILES_Y; // 960
export const ATTRIBUTE_TABLE_BYTES = 64;
export const PALETTE_BYTES = 32;
export const OAM_BYTES = 256; // 64 sprites × 4 bytes

export type Mirroring = "horizontal" | "vertical";

export interface NesPpuState {
    /**
     * Two pattern tables; each is 4096 bytes representing 256 tiles of 16
     * bytes (the first 8 bytes are bitplane 0, the last 8 are bitplane 1).
     */
    patternTables: [Uint8Array, Uint8Array];
    /** 32 × 30 tile indices into the active background pattern table. */
    nametable: Uint8Array;
    /**
     * Attribute table: 64 bytes (8 × 8). Each byte covers a 32×32 px region;
     * 2 bits per 16×16 quadrant select a background subpalette (0..3).
     * Bit layout per byte: `(bottom-right << 6) | (bottom-left << 4) | (top-right << 2) | top-left`.
     */
    attribute: Uint8Array;
    /**
     * 32-byte palette RAM. Each byte is an index 0..63 into the master palette.
     * - palette[0]: universal background color
     * - palette[1..3]: bg subpalette 0 (3 colors)
     * - palette[5..7]: bg subpalette 1
     * - palette[9..11]: bg subpalette 2
     * - palette[13..15]: bg subpalette 3
     * - palette[17..19]: sprite subpalette 0
     * - palette[21..23]: sprite subpalette 1
     * - palette[25..27]: sprite subpalette 2
     * - palette[29..31]: sprite subpalette 3
     * (Bytes 4, 8, 12 mirror palette[0]; bytes 16, 20, 24, 28 also mirror it.)
     */
    palette: Uint8Array;
    /** Coarse + fine scroll, 0..255 / 0..239. */
    scrollX: number;
    scrollY: number;
    /** Which pattern table the background reads from. */
    bgPatternTable: 0 | 1;
    /** Which pattern table sprites read from (8×8 mode). */
    sprPatternTable: 0 | 1;
    /**
     * 64 sprites × 4 bytes:
     *   byte0 = y-1 (real PPU is delayed by 1 line; 0xFF = hidden)
     *   byte1 = tile index
     *   byte2 = attributes:
     *           bits 0..1 = sprite subpalette (0..3)
     *           bit 5     = priority (1 = behind background)
     *           bit 6     = horizontal flip
     *           bit 7     = vertical flip
     *   byte3 = x
     */
    oam: Uint8Array;
    mirroring: Mirroring;
}

/** Allocate an empty (all zero) state. Universal bg defaults to a dark blue. */
export function createEmptyPpuState(): NesPpuState {
    const palette = new Uint8Array(PALETTE_BYTES);
    palette[0] = 0x0F; // black-ish universal bg by default
    palette[1] = 0x01;
    palette[2] = 0x21;
    palette[3] = 0x31;
    palette[5] = 0x06;
    palette[6] = 0x16;
    palette[7] = 0x26;
    palette[9] = 0x09;
    palette[10] = 0x19;
    palette[11] = 0x29;
    palette[13] = 0x0C;
    palette[14] = 0x1C;
    palette[15] = 0x2C;
    palette[17] = 0x06;
    palette[18] = 0x16;
    palette[19] = 0x36;
    palette[21] = 0x02;
    palette[22] = 0x12;
    palette[23] = 0x22;
    palette[25] = 0x08;
    palette[26] = 0x18;
    palette[27] = 0x38;
    palette[29] = 0x14;
    palette[30] = 0x24;
    palette[31] = 0x34;
    return {
        patternTables: [
            new Uint8Array(PATTERN_TABLE_BYTES),
            new Uint8Array(PATTERN_TABLE_BYTES),
        ],
        nametable: new Uint8Array(NAMETABLE_TILES),
        attribute: new Uint8Array(ATTRIBUTE_TABLE_BYTES),
        palette,
        scrollX: 0,
        scrollY: 0,
        bgPatternTable: 0,
        sprPatternTable: 1,
        oam: new Uint8Array(OAM_BYTES).fill(0xFF),
        mirroring: "horizontal",
    };
}

/** Deep clone (used by glitch ops to keep state immutable from React's POV). */
export function clonePpuState(state: NesPpuState): NesPpuState {
    return {
        patternTables: [
            new Uint8Array(state.patternTables[0]),
            new Uint8Array(state.patternTables[1]),
        ],
        nametable: new Uint8Array(state.nametable),
        attribute: new Uint8Array(state.attribute),
        palette: new Uint8Array(state.palette),
        scrollX: state.scrollX,
        scrollY: state.scrollY,
        bgPatternTable: state.bgPatternTable,
        sprPatternTable: state.sprPatternTable,
        oam: new Uint8Array(state.oam),
        mirroring: state.mirroring,
    };
}

/**
 * Read one of the four 2-bit subpalette selectors out of an attribute byte
 * for the cell at (tileX, tileY). The attribute table covers the screen in
 * 32×32 px chunks (4×4 tiles); each chunk's byte is split into four 16×16
 * quadrants.
 */
export function attributeSubpalette(
    attribute: Uint8Array,
    tileX: number,
    tileY: number,
): number {
    const ax = Math.min(7, tileX >> 2);
    const ay = Math.min(7, tileY >> 2);
    const byte = attribute[ay * 8 + ax] ?? 0;
    const quadX = (tileX >> 1) & 1;
    const quadY = (tileY >> 1) & 1;
    const shift = ((quadY << 1) | quadX) * 2;
    return (byte >> shift) & 0x03;
}

/**
 * Resolve a pattern-table 2-bit pixel value to a master palette index for a
 * background pixel of subpalette `sub`. Pixel value 0 always means "use the
 * universal background color" (palette[0]).
 */
function bgPaletteLookup(
    palette: Uint8Array,
    sub: number,
    pixel: number,
): number {
    if (pixel === 0) return palette[0] & 0x3F;
    const idx = (sub * 4 + pixel) & 0x1F;
    return palette[idx] & 0x3F;
}

function sprPaletteLookup(
    palette: Uint8Array,
    sub: number,
    pixel: number,
): number | null {
    if (pixel === 0) return null; // transparent
    const idx = (16 + sub * 4 + pixel) & 0x1F;
    return palette[idx] & 0x3F;
}

/**
 * Read the 2-bit color value of pixel (px, py) within tile `tileIndex` in the
 * given pattern table. (px, py are 0..7.)
 */
export function readTilePixel(
    pattern: Uint8Array,
    tileIndex: number,
    px: number,
    py: number,
): number {
    const base = (tileIndex & 0xFF) * 16;
    const lo = pattern[base + py] ?? 0;
    const hi = pattern[base + 8 + py] ?? 0;
    const bit = 7 - px;
    return ((lo >> bit) & 1) | (((hi >> bit) & 1) << 1);
}

/** Output of the renderer: indexed framebuffer + an opaque RGBA framebuffer. */
export interface RenderedFrame {
    /** 256 × 240 master-palette indices (0..63). */
    indexed: Uint8Array;
    /**
     * 256 × 240 packed `(255<<24)|(r<<16)|(g<<8)|b` values. Always alpha 255 so
     * the universal background color is preserved when round-tripped through
     * `pixelSvgRender` (which treats alpha 0 as empty).
     */
    rgba: Uint32Array;
}

/**
 * Render a single frame from a static PPU state.
 *
 * Pipeline:
 *  1. Fill with the universal background.
 *  2. Composite background tiles using `scrollX`/`scrollY` and mirroring.
 *  3. Composite sprites (8×8 only). Sprites with priority bit set draw
 *     behind opaque (non-zero) background pixels; others draw on top.
 *
 * Sprites with `y == 0xFF` are treated as off-screen.
 */
export function renderFrame(state: NesPpuState): RenderedFrame {
    const W = NES_SCREEN_WIDTH;
    const H = NES_SCREEN_HEIGHT;
    const indexed = new Uint8Array(W * H);
    const universalBg = state.palette[0] & 0x3F;
    indexed.fill(universalBg);

    // Track whether each background pixel was opaque (pixel value > 0). We need
    // this for sprite-vs-bg priority.
    const bgOpaque = new Uint8Array(W * H);

    const bgPattern = state.patternTables[state.bgPatternTable];
    const sx = ((state.scrollX % 256) + 256) % 256;
    const sy = ((state.scrollY % 240) + 240) % 240;
    const horizontalMirror = state.mirroring === "horizontal";

    // Background. We iterate every screen pixel and pull from a virtual
    // (potentially mirrored) nametable surface. Cheap because nametable is
    // only 32×30 — the inner work is just two byte reads + a couple of shifts.
    for (let y = 0; y < H; y++) {
        const sourceY = (y + sy) % H;
        const tileY = Math.floor(sourceY / 8);
        const tilePy = sourceY & 7;
        for (let x = 0; x < W; x++) {
            const sourceX = (x + sx) % W;
            const tileX = Math.floor(sourceX / 8);
            const tilePx = sourceX & 7;
            const ntIndex = tileY * NAMETABLE_TILES_X + tileX;
            // mirroring is decorative here (single nametable); we re-use the
            // "wrap" semantics but apply mirroring by optionally flipping the
            // tile coordinate when crossing the screen edge during scroll.
            // For a static frame this barely matters but it surfaces the toggle
            // visually for users.
            let mirroredNt = state.nametable[ntIndex] ?? 0;
            if (horizontalMirror) {
                // no change: horizontal mirroring duplicates the screen left↔right,
                // which equals the wrap we already do.
            } else {
                // vertical mirroring: when the source crosses the bottom half,
                // re-read the same tile but on a flipped row.
                if (sourceY >= H / 2) {
                    const tileYAlt = Math.floor((H - 1 - sourceY) / 8);
                    const altIndex = tileYAlt * NAMETABLE_TILES_X + tileX;
                    mirroredNt = state.nametable[altIndex] ?? 0;
                }
            }
            const pixel = readTilePixel(bgPattern, mirroredNt, tilePx, tilePy);
            const sub = attributeSubpalette(state.attribute, tileX, tileY);
            const masterIdx = bgPaletteLookup(state.palette, sub, pixel);
            const o = y * W + x;
            indexed[o] = masterIdx;
            bgOpaque[o] = pixel === 0 ? 0 : 1;
        }
    }

    // Sprites (8×8 only). Iterate in OAM order; later sprites in the table
    // appear behind earlier ones when they overlap (real PPU does the same).
    const sprPattern = state.patternTables[state.sprPatternTable];
    for (let i = 0; i < 64; i++) {
        const o = i * 4;
        const yByte = state.oam[o];
        if (yByte >= 0xEF) continue; // off-screen
        const y0 = (yByte + 1) & 0xFF;
        const tileIndex = state.oam[o + 1];
        const attr = state.oam[o + 2];
        const x0 = state.oam[o + 3];
        const sub = attr & 0x03;
        const behindBg = (attr & 0x20) !== 0;
        const flipH = (attr & 0x40) !== 0;
        const flipV = (attr & 0x80) !== 0;
        for (let py = 0; py < 8; py++) {
            const sy2 = y0 + py;
            if (sy2 < 0 || sy2 >= H) continue;
            const sourcePy = flipV ? 7 - py : py;
            for (let px = 0; px < 8; px++) {
                const sx2 = x0 + px;
                if (sx2 < 0 || sx2 >= W) continue;
                const sourcePx = flipH ? 7 - px : px;
                const pixel = readTilePixel(
                    sprPattern,
                    tileIndex,
                    sourcePx,
                    sourcePy,
                );
                if (pixel === 0) continue;
                const idx = sy2 * W + sx2;
                if (behindBg && bgOpaque[idx]) continue;
                const masterIdx = sprPaletteLookup(state.palette, sub, pixel);
                if (masterIdx === null) continue;
                indexed[idx] = masterIdx;
            }
        }
    }

    const rgba = indexedToRgba(indexed);
    return { indexed, rgba };
}

/** Pack a master-palette indexed buffer into an opaque-RGBA `Uint32Array`. */
export function indexedToRgba(indexed: Uint8Array): Uint32Array {
    const out = new Uint32Array(indexed.length);
    for (let i = 0; i < indexed.length; i++) {
        out[i] = nesIndexToPackedRgba(indexed[i]);
    }
    return out;
}
